import { coreQueryKey } from '@/api/core/authorization';
import {
  changeAdminUserAuthorization,
  readAdminUserAuthorization,
  type AdminUserAuthorization,
} from '@/api/core/users';
import { LoadFailure } from '@/components/load-failure';
import { actor, commandOwner } from '@/pages/property/presentation/command-owner';
import { useCurrentCommand } from '@/pages/property/presentation/use-current-command';
import {
  Button,
  Checkbox,
  CheckboxLabel,
  DetailPage,
  DirtyConfirmDialog,
  FormField,
  Textarea,
  useDirtyConfirm,
} from '@mgrvtechgroup/admin-ds';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  domainMaxMask,
  draftFrom,
  MAX_OVERRIDES,
  sameDraft,
  validateDraft,
  type AuthorizationDraft,
} from './user-authorization-draft';
import {
  capabilityLabels,
  DOMAIN_CAPABILITIES,
  DOMAIN_LABELS,
  DOMAIN_ORDER,
  USER_ROLES,
  userRoleLabels,
} from './user-presentation';
import type { UserRuntime } from './user-queries';

export function UserAuthorizationSection({
  runtime,
  userId,
  canEdit,
  onDirtyChange,
}: {
  runtime: UserRuntime;
  userId: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const scope = `user-authorization:${userId}`;
  const cmd = useCurrentCommand<
    AdminUserAuthorization,
    Parameters<typeof changeAdminUserAuthorization>[2]
  >({
    runtime,
    scope,
    bits: 1,
    domain: 'user',
    read: async () => {
      const value = await readAdminUserAuthorization(runtime.client, userId);
      const attempt = commandOwner(runtime, scope).attempt;
      const accepted = attempt?.accepted
        ? (attempt.body as Parameters<typeof changeAdminUserAuthorization>[2])
        : undefined;
      if (
        accepted &&
        !sameDraft(value, { roleBits: accepted.role_bits, overrides: accepted.domain_overrides })
      )
        throw new Error('저장된 권한이 요청과 다릅니다. 현재 권한을 다시 조회해 주세요.');
      return value;
    },
    validate: () => Promise.resolve(),
    write: async (body: Parameters<typeof changeAdminUserAuthorization>[2]) => {
      const me = await actor(runtime, 1, 'user');
      await changeAdminUserAuthorization(
        runtime.client,
        userId,
        body,
        cmd.current?.registry_version ?? null,
      );
      void queryClient.invalidateQueries({
        queryKey: coreQueryKey(
          runtime.apiNamespace,
          runtime.session.getSnapshot().generation,
          'user-detail',
          userId,
        ),
      });
      // 자기 권한을 바꾼 경우 이후 화면은 서버가 다시 계산한 현재 권한으로만 판단한다.
      if (me === userId) await runtime.session.loadCurrentAuthorization();
    },
  });
  const current = cmd.current;
  const [editing, setEditing] = useState(false);
  const editTrigger = useRef<HTMLButtonElement>(null);
  const editForm = useRef<HTMLFieldSetElement>(null);
  const [draft, setDraft] = useState<AuthorizationDraft | null>(null);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [touched, setTouched] = useState(false);
  const edits = useRef(0);
  const confirmRegion = useRef<HTMLDivElement>(null);
  const confirmTrigger = useRef<HTMLButtonElement>(null);
  const revision = current?.revision ?? null;
  useEffect(() => {
    // 같은 대상의 새 현재값이 오면 초안을 현재값에서 다시 시작한다.
    const before = edits.current;
    queueMicrotask(() => {
      if (edits.current !== before) return;
      setDraft(current ? draftFrom(current) : null);
      setReason('');
      setConfirm(false);
      setTouched(false);
      if (!current) setEditing(false);
    });
  }, [current, revision]);
  useEffect(() => {
    if (confirm) confirmRegion.current?.focus();
  }, [confirm]);

  const locked = cmd.busy || !!cmd.attempt || !cmd.canWrite || !canEdit;
  const errors = current && draft ? validateDraft(current, draft, reason) : {};
  const unchanged = !!current && !!draft && sameDraft(current, draft);
  const dirty =
    canEdit && !!current && !!draft && (!unchanged || reason.length > 0 || !!cmd.attempt);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const dirtyGuard = useDirtyConfirm(dirty);
  useEffect(() => {
    if (editing) editForm.current?.querySelector<HTMLElement>('[role=checkbox]')?.focus();
  }, [editing]);
  const closeEditor = () => {
    edits.current++;
    setDraft(current ? draftFrom(current) : null);
    setReason('');
    setConfirm(false);
    setTouched(false);
    setEditing(false);
    // The discard dialog restores focus after closing; the editor trigger stays mounted.
    setTimeout(() => editTrigger.current?.focus(), 0);
  };
  const set = (next: AuthorizationDraft) => {
    edits.current++;
    setDraft(next);
    setConfirm(false);
    setTouched(true);
  };

  return (
    <DetailPage.Section title="역할과 권한">
      <DirtyConfirmDialog
        open={dirtyGuard.confirmOpen}
        onOpenChange={(open) => {
          if (!open) dirtyGuard.onCancel();
        }}
        onConfirm={dirtyGuard.onConfirm}
      />
      {cmd.error && !current && (
        <LoadFailure
          error={new Error(cmd.error)}
          subject="사용자 권한"
          onRetry={() => void cmd.refresh()}
        />
      )}
      {cmd.error && current && <p role="alert">{cmd.error}</p>}
      {cmd.success && <p role="status">저장 후 현재 권한을 다시 확인했습니다.</p>}
      {!current && !cmd.error && <p role="status">현재 권한을 조회하고 있습니다.</p>}
      {current && (
        <div className="space-y-4">
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="caption text-muted-foreground">서비스 역할</dt>
              <dd>
                {current.assigned ? userRoleLabels(current.role_bits).join(', ') : '권한 미배정'}
              </dd>
            </div>
          </dl>
          <ul className="space-y-1">
            {DOMAIN_ORDER.map((domain) => {
              const bits = current.domain_bits[domain];
              const explicit = domain in current.domain_overrides;
              return (
                <li key={domain}>
                  <span className="font-medium">{DOMAIN_LABELS[domain]}</span>{' '}
                  <span className="text-muted-foreground">
                    {bits === undefined
                      ? current.assigned
                        ? '권한 없음'
                        : '미배정'
                      : `${explicit ? '직접 지정' : '역할 기준'}: ${capabilityLabels(domain, bits).join(', ') || '권한 없음'}`}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="caption text-muted-foreground">
            표시된 권한은 서버가 계산한 현재 값입니다. 저장 후 같은 조회로 다시 확인합니다.
          </p>
          <Button variant="outline" disabled={cmd.busy} onClick={() => void cmd.refresh()}>
            현재 권한 다시 조회
          </Button>
          {canEdit && draft && (
            <Button
              ref={editTrigger}
              variant="outline"
              disabled={locked || editing}
              aria-expanded={editing}
              aria-controls={`auth-editor-${userId}`}
              onClick={() => {
                setEditing(true);
              }}
            >
              권한 변경
            </Button>
          )}
          {canEdit && draft && editing && (
            <fieldset
              ref={editForm}
              id={`auth-editor-${userId}`}
              className="space-y-3 border-t pt-4"
              disabled={locked}
            >
              <legend className="font-semibold">권한 변경</legend>
              <div className="flex flex-wrap gap-4">
                {USER_ROLES.map(({ bit, label }) => (
                  <CheckboxLabel key={bit} className="flex items-center gap-2">
                    <Checkbox
                      checked={(draft.roleBits & bit) !== 0}
                      onCheckedChange={(checked) => {
                        set({
                          ...draft,
                          roleBits: checked ? draft.roleBits | bit : draft.roleBits & ~bit,
                        });
                      }}
                    />
                    {label}
                  </CheckboxLabel>
                ))}
              </div>
              {errors.role && <p role="alert">{errors.role}</p>}
              {current.assigned && (
                <div className="space-y-3">
                  <p className="caption text-muted-foreground">
                    영역별 직접 지정은 역할 기준 권한을 대신합니다(최대 {MAX_OVERRIDES}개).
                  </p>
                  {DOMAIN_ORDER.filter((d) => domainMaxMask(d, current.registry_version) > 0).map(
                    (domain) => {
                      const explicit = draft.overrides[domain];
                      return (
                        <div key={domain} className="space-y-1">
                          <CheckboxLabel className="flex items-center gap-2 font-medium">
                            <Checkbox
                              checked={explicit !== undefined}
                              onCheckedChange={(checked) => {
                                const rest = Object.fromEntries(
                                  Object.entries(draft.overrides).filter(([key]) => key !== domain),
                                );
                                set({
                                  ...draft,
                                  overrides: checked
                                    ? { ...rest, [domain]: current.domain_bits[domain] ?? 0 }
                                    : rest,
                                });
                              }}
                            />
                            {DOMAIN_LABELS[domain]} 직접 지정
                          </CheckboxLabel>
                          {explicit !== undefined && (
                            <div className="flex flex-wrap gap-4 pl-6">
                              {(DOMAIN_CAPABILITIES[domain] ?? [])
                                .filter(
                                  ({ bit }) =>
                                    (domainMaxMask(domain, current.registry_version) & bit) !== 0,
                                )
                                .map(({ bit, label }) => (
                                  <CheckboxLabel key={bit} className="flex items-center gap-2">
                                    <Checkbox
                                      checked={(explicit & bit) !== 0}
                                      onCheckedChange={(checked) => {
                                        set({
                                          ...draft,
                                          overrides: {
                                            ...draft.overrides,
                                            [domain]: checked ? explicit | bit : explicit & ~bit,
                                          },
                                        });
                                      }}
                                    />
                                    {label}
                                  </CheckboxLabel>
                                ))}
                              {explicit === 0 && (
                                <span className="caption text-muted-foreground">
                                  권한을 모두 해제하면 이 영역 권한이 없어집니다.
                                </span>
                              )}
                            </div>
                          )}
                          {errors[`domain:${domain}`] && (
                            <p role="alert">{errors[`domain:${domain}`]}</p>
                          )}
                        </div>
                      );
                    },
                  )}
                  {errors.domains && <p role="alert">{errors.domains}</p>}
                </div>
              )}
              <FormField label="변경 사유" htmlFor={`auth-reason-${userId}`}>
                <Textarea
                  id={`auth-reason-${userId}`}
                  value={reason}
                  maxLength={500}
                  onChange={(event) => {
                    edits.current++;
                    setReason(event.target.value);
                    setConfirm(false);
                  }}
                />
              </FormField>
              {touched && errors.reason && <p role="alert">{errors.reason}</p>}
              <Button
                ref={confirmTrigger}
                disabled={unchanged || Object.keys(errors).length > 0}
                onClick={() => {
                  setConfirm(true);
                }}
              >
                변경 내용 확인
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  dirtyGuard.guardedClose(closeEditor);
                }}
              >
                권한 편집 취소
              </Button>
              {unchanged && <p className="caption text-muted-foreground">현재 권한과 같습니다.</p>}
            </fieldset>
          )}
          {confirm && draft && (
            <div
              ref={confirmRegion}
              tabIndex={-1}
              role="region"
              aria-label="권한 변경 확인"
              className="space-y-2 rounded border p-3"
            >
              <p className="break-all">대상 사용자: {userId}</p>
              <p>
                {userRoleLabels(current.assigned ? current.role_bits : null).join(', ')} →{' '}
                {userRoleLabels(draft.roleBits).join(', ')}
              </p>
              <ul className="space-y-1">
                {DOMAIN_ORDER.filter(
                  (domain) => current.domain_overrides[domain] !== draft.overrides[domain],
                ).map((domain) => {
                  const describe = (bits: number | undefined) =>
                    bits === undefined
                      ? '역할 기준'
                      : capabilityLabels(domain, bits).join(', ') || '권한 없음';
                  return (
                    <li key={domain}>
                      {DOMAIN_LABELS[domain]}: {describe(current.domain_overrides[domain])} →{' '}
                      {describe(draft.overrides[domain])}
                    </li>
                  );
                })}
              </ul>
              <p className="caption text-muted-foreground">
                사유: {reason.trim()} · 저장하면 이 사용자의 권한이 바로 바뀝니다. 본인 권한이면
                이후 화면은 다시 계산된 권한으로 표시됩니다.
              </p>
              <Button
                disabled={locked}
                onClick={() =>
                  void cmd.submit({
                    command_id: crypto.randomUUID(),
                    expected_revision: current.revision ?? 0,
                    role_bits: draft.roleBits,
                    domain_overrides: draft.overrides,
                    reason: reason.trim(),
                  })
                }
              >
                확인하고 권한 저장
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setConfirm(false);
                  confirmTrigger.current?.focus();
                }}
              >
                계속 수정
              </Button>
            </div>
          )}
          {cmd.attempt && !cmd.attempt.accepted && (
            <div className="space-y-2">
              <p role="alert">
                저장 결과를 확인하지 못했습니다. 같은 요청의 결과를 먼저 확인합니다.
              </p>
              <Button disabled={cmd.busy} onClick={() => void cmd.submit()}>
                원래 요청 결과 확인
              </Button>
            </div>
          )}
        </div>
      )}
    </DetailPage.Section>
  );
}
